import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Stack,
  Grid,
  Avatar,
  TextField,
  MenuItem,
  Drawer,
  Divider,
  Chip,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import StarRateIcon from '@mui/icons-material/StarRate';
import PercentIcon from '@mui/icons-material/Percent';
import PaidIcon from '@mui/icons-material/Paid';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DetailItem from './DetailItem';
import { getRoleInfo } from './roleUtils';

const AdminDetailsDrawer = ({
  open,
  onClose,
  selectedAdmin,
  editData,
  isEditMode,
  setIsEditMode,
  onEditChange,
  onFileChange,
  previewUrl,
  setPreviewUrl,
  getFullImageUrl,
  onSubmit,
  isSubmitting,
}) => {
  if (!selectedAdmin) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }}
      PaperProps={{
        className: 'drawer-paper',
        sx: { width: { xs: '100%', sm: 550 } },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box className="drawer-header">
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#2B3674' }}>
            {isEditMode ? 'Modifier Profil' : 'Détails Membre'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
          <Paper
            elevation={0}
            sx={{ p: 4, borderRadius: '25px', textAlign: 'center', mb: 3 }}
          >
            <Avatar
              src={getFullImageUrl(previewUrl)}
              className="profile-avatar-large"
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                border: '4px solid #F4F7FE',
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#2B3674' }}>
              {editData.nom_user} {editData.prenom_user}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 1 }}
            >
              <Chip
                label={getRoleInfo(editData.role_user).label}
                sx={{
                  fontWeight: 700,
                  bgcolor: getRoleInfo(editData.role_user).bgcolor,
                  color: getRoleInfo(editData.role_user).color,
                }}
              />
              <Chip
                icon={<StarRateIcon sx={{ '&&': { color: '#FFB547' } }} />}
                label={`${editData.total_reviews_user || 0} Avis`}
                sx={{ fontWeight: 700, bgcolor: '#FFF9F0', color: '#FFB547' }}
              />
            </Stack>
          </Paper>

          {isEditMode ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ textAlign: 'center', mb: 2 }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload"
                  type="file"
                  onChange={onFileChange}
                />
                <label htmlFor="photo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<PhotoCamera />}
                  >
                    Changer la photo
                  </Button>
                </label>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Nom"
                  name="nom_user"
                  fullWidth
                  value={editData.nom_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Prénom"
                  name="prenom_user"
                  fullWidth
                  value={editData.prenom_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  name="email_user"
                  fullWidth
                  value={editData.email_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Disponibilité"
                  name="availability_user"
                  fullWidth
                  value={editData.availability_user ?? 0}
                  onChange={onEditChange}
                  SelectProps={{ MenuProps: { sx: { zIndex: 3000 } } }}
                >
                  <MenuItem value={1}>En ligne</MenuItem>
                  <MenuItem value={0}>Sur RDV</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Commission"
                  name="commission"
                  type="number"
                  fullWidth
                  value={editData.commission || ''}
                  onChange={onEditChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Lien Boutique"
                  name="lien_boutique"
                  fullWidth
                  value={editData.lien_boutique || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Titre"
                  name="title_user"
                  fullWidth
                  value={editData.title_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Téléphone"
                  name="phone_user"
                  fullWidth
                  value={editData.phone_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Adresse"
                  name="addresse_user"
                  fullWidth
                  value={editData.addresse_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Ville"
                  name="ville_user"
                  fullWidth
                  value={editData.ville_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="CP"
                  name="cp_user"
                  fullWidth
                  value={editData.cp_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Province"
                  name="province_user"
                  fullWidth
                  value={editData.province_user || ''}
                  onChange={onEditChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Spécialité"
                  name="main_speciality_user"
                  fullWidth
                  placeholder="ex: Design, Marketing, Web"
                  InputLabelProps={{ shrink: true }}
                  value={editData.main_speciality_user || ''}
                  onChange={onEditChange}
                  helperText="Vous pouvez ajouter plusieurs spécialités en les séparant par une virgule (,)"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': { borderColor: '#E0E5F2' },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#2B3674',
                      fontWeight: 700,
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#A3AED0',
                      fontSize: '0.8rem',
                      marginTop: '4px',
                    },
                  }}
                />
              </Grid>

             {/* Informations Plateforme (Affiché pour Intervenant et Praticien) */}
{[3, 4].includes(Number(editData.role_user)) && (
  <>
    <Grid item xs={12}>
      <Typography sx={{ color: '#2B3674', fontWeight: 800, fontSize: '14px', mt: 1 }}>
        Informations Plateforme
      </Typography>
    </Grid>
    <Grid item xs={4}>
      <TextField
        label="Prix Soin"
        name="prix_soa"
        type="number"
        fullWidth
        value={editData.prix_soa ?? editData.praticien_info?.prix_soa ?? ''}
        onChange={onEditChange}
        InputProps={{
          endAdornment: <InputAdornment position="end">€</InputAdornment>,
        }}
      />
    </Grid>
    <Grid item xs={4}>
      <TextField
        label="Commission Consultation"
        name="commission_consultation"
        type="number"
        fullWidth
        value={editData.commission_consultation ?? editData.praticien_info?.commission_consultation ?? ''}
        onChange={onEditChange}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
      />
    </Grid>
    <Grid item xs={4}>
      <TextField
        label="Commission Vidéo"
        name="commission_video"
        type="number"
        fullWidth
        value={editData.commission_video ?? editData.praticien_info?.commission_video ?? ''}
        onChange={onEditChange}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
      />
    </Grid>
  </>
)}

              <Grid item xs={12} sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(editData.recreate_zoom_account)}
                      onChange={onEditChange}
                      name="recreate_zoom_account"
                      color="primary"
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        color: '#2B3674',
                        fontWeight: 600,
                        fontSize: '14px',
                      }}
                    >
                      Recréer le compte Zoom
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          ) : (
            <Stack spacing={3}>
              <Box className="detail-card">
                <Typography
                  sx={{
                    color: '#2B3674',
                    fontWeight: 800,
                    mb: 1,
                    fontSize: '15px',
                  }}
                >
                  Finance & Boutique
                </Typography>
                <DetailItem
                  icon={<PercentIcon />}
                  label="Commission Plateforme"
                  value={`${selectedAdmin.commission || 0}%`}
                  color="#05CD99"
                />
                <Divider />
                <DetailItem
                  icon={<StorefrontIcon />}
                  label="Lien Boutique"
                  value={selectedAdmin.lien_boutique}
                  color="#4318FF"
                />
              </Box>

              <Box className="detail-card">
                <Typography
                  sx={{
                    color: '#2B3674',
                    fontWeight: 800,
                    mb: 1,
                    fontSize: '15px',
                  }}
                >
                  Contact & Statut
                </Typography>
                <DetailItem
                  icon={<AccessTimeIcon />}
                  label="Statut"
                  value={
                    selectedAdmin.availability_user === 1
                      ? 'En ligne'
                      : 'Sur RDV'
                  }
                  color={
                    selectedAdmin.availability_user === 1
                      ? '#05CD99'
                      : '#EE5D50'
                  }
                />
                <Divider />
                <DetailItem
                  icon={<LocalPhoneIcon />}
                  label="Téléphone"
                  value={selectedAdmin.phone_user}
                />
                <Divider />
                <DetailItem
                  icon={<LocationOnIcon />}
                  label="Localisation"
                  value={`${selectedAdmin.ville_user || ''}, ${selectedAdmin.cp_user || ''}`}
                />
                <Divider />
                <DetailItem
                  icon={<BadgeIcon />}
                  label="Spécialité"
                  value={selectedAdmin.main_speciality_user}
                />
                <Divider />
                <DetailItem
                  icon={<EventNoteIcon />}
                  label="Inscription"
                  value={
                    selectedAdmin.date_inscrip_user
                      ? new Date(
                          selectedAdmin.date_inscrip_user
                        ).toLocaleDateString()
                      : '—'
                  }
                />
              </Box>

              {/* Titre mis à jour : Informations Plateforme */}
              <Box className="detail-card">
                <Typography
                  sx={{
                    color: '#2B3674',
                    fontWeight: 800,
                    mb: 1,
                    fontSize: '15px',
                  }}
                >
                  Informations Plateforme
                </Typography>
                <DetailItem
                  icon={<MedicalServicesIcon />}
                  label="Prix Soin"
                  value={`${selectedAdmin.praticien_info?.prix_soa || 0} €`}
                  color="#4318FF"
                />
                <Divider />
                <DetailItem
                  icon={<PaidIcon />}
                  label="Commission Consultation"
                  value={`${selectedAdmin.praticien_info?.commission_consultation || 0}%`}
                  color="#05CD99"
                />
                <Divider />
                <DetailItem
                  icon={<VideoCameraFrontIcon />}
                  label="Commission Vidéo"
                  value={`${selectedAdmin.praticien_info?.commission_video || 0}%`}
                  color="#05CD99"
                />
              </Box>

              <Box className="detail-card">
                <Typography
                  sx={{
                    color: '#2B3674',
                    fontWeight: 800,
                    mb: 1,
                    fontSize: '15px',
                  }}
                >
                  Biographie
                </Typography>
                <Typography
                  sx={{ color: '#707EAE', fontSize: '14px', lineHeight: 1.6 }}
                >
                  {selectedAdmin.description_user || 'Aucune description.'}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>

        <Box className="drawer-footer">
          <Button
            fullWidth
            variant={isEditMode ? 'contained' : 'outlined'}
            onClick={isEditMode ? onSubmit : () => setIsEditMode(true)}
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              borderRadius: '12px',
              fontWeight: 700,
              bgcolor: isEditMode ? '#4318FF' : 'transparent',
              color: isEditMode ? 'white' : '#4318FF',
            }}
          >
            {isEditMode
              ? isSubmitting
                ? 'Enregistrement...'
                : 'Sauvegarder'
              : 'Modifier'}
          </Button>
          {isEditMode && (
            <Button
              fullWidth
              sx={{ mt: 1, color: '#EE5D50' }}
              onClick={() => {
                setIsEditMode(false);
                setPreviewUrl(selectedAdmin.photo_user);
              }}
            >
              Annuler
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AdminDetailsDrawer;
